<template>
  <div class="roles-settings">
    <!-- Access Denied Message -->
    <div v-if="!hasPermission('admin.roles.index')" class="d-flex justify-center align-center" style="min-height: 400px;">
      <v-card max-width="400" class="text-center pa-6">
        <v-icon size="64" color="error" class="mb-4">mdi-shield-off</v-icon>
        <h2 class="text-h5 mb-2">ليس لديك صلاحية</h2>
        <p class="text-body-1 text-grey">
          ليس لديك صلاحية للوصول إلى إدارة الأدوار
        </p>
      </v-card>
    </div>

    <!-- Main Content -->
    <v-container v-else fluid>
      <!-- Page Header -->
      <div class="d-flex justify-space-between align-center mb-6">
        <div>
          <h1 class="text-h4 font-weight-bold mb-2">إدارة الأدوار</h1>
          <p class="text-body-1 text-grey">إدارة الأدوار والصلاحيات في النظام</p>
        </div>
      </div>

      <!-- Content Card -->
      <v-card>
        <v-card-text class="pa-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <h2 class="text-h5">قائمة الأدوار</h2>
            <v-btn
              v-if="hasPermission('admin.roles.store')"
              v-action="{ module: 'roles', action: 'store' }"
              color="primary"
              prepend-icon="mdi-plus"
              @click="openAddRoleDialog"
            >
              إضافة دور جديد
            </v-btn>
          </div>

          <v-data-table
            :headers="roleHeaders"
            :items="roles"
            :search="roleSearch"
            :loading="loading"
            color="primary"
            first-icon="mdi-chevron-left"
            last-icon="mdi-chevron-right"
            next-icon="mdi-chevron-right"
            prev-icon="mdi-chevron-left"
            show-current-page
            show-first-last-page
            show-first-page
            show-last-page
            show-next-page
            show-previous-page
            class="elevation-1"
          >
            <template #top>
              <v-text-field
                v-model="roleSearch"
                prepend-inner-icon="mdi-magnify"
                label="بحث في الأدوار"
                variant="outlined"
                density="compact"
                class="mx-4 mt-4"
              ></v-text-field>
            </template>

            <template v-slot:[`item.permissions`]="{ item }">
              <v-chip color="primary" size="small" variant="tonal">
                {{ getPermissionCount(item) }} صلاحية
              </v-chip>
              <v-tooltip activator="parent" location="top">
                <div style="max-width: 300px;">
                  <strong>الصلاحيات:</strong><br>
                  {{ getPermissionsList(item) }}
                </div>
              </v-tooltip>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                v-if="hasPermission('admin.roles.update')"
                v-action="{ module: 'roles', action: 'update' }"
                icon
                size="small"
                color="primary"
                variant="text"
                @click="editRole(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                v-if="hasPermission('admin.roles.destroy')"
                v-action="{ module: 'roles', action: 'destroy' }"
                icon
                size="small"
                color="error"
                variant="text"
                @click="confirmDelete(item, 'role')"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Role Dialog (simplified version) -->
    <v-dialog v-model="showRoleDialog" max-width="700px">
      <v-card>
        <v-card-title>
          {{ editingRole ? 'تعديل الدور' : 'إضافة دور جديد' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="roleForm" @submit.prevent="saveRole">
            <v-text-field
              v-model="roleForm.name"
              label="اسم الدور"
              variant="outlined"
              required
              class="mb-4"
            ></v-text-field>
            
            <v-textarea
              v-model="roleForm.desc"
              label="الوصف"
              variant="outlined"
              rows="3"
              class="mb-4"
            ></v-textarea>
            
            <!-- Permissions Section -->
            <div class="mb-4">
              <h3 class="text-h6 mb-3">الصلاحيات</h3>
              
              <!-- Loading State -->
              <div v-if="loadingPermissions" class="text-center py-4">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                <p class="text-body-2 mt-2">جاري تحميل الصلاحيات...</p>
              </div>
              
              <!-- Empty State -->
              <div v-else-if="Object.keys(availablePermissions).length === 0" class="text-center py-4">
                <v-icon size="48" color="grey" class="mb-2">mdi-shield-off</v-icon>
                <p class="text-body-2 text-grey">لا توجد صلاحيات متاحة</p>
              </div>
              
              <!-- Permissions Content -->
              <div v-else>
                <!-- Debug info -->
                <div class="mb-2 text-caption text-grey">
                  Debug: Available permissions keys: {{ Object.keys(availablePermissions).length }}
                  | Selected permissions: {{ roleForm.permissions.length }}
                </div>
                
                <div v-if="Object.keys(availablePermissions).length === 0" class="text-center py-4">
                  <p class="text-body-2 text-grey">لا توجد صلاحيات للعرض</p>
                </div>
                
                <!-- Tree Structure -->
                <div v-else class="permissions-tree">
                  <div
                    v-for="(actions, moduleName) in availablePermissions"
                    :key="moduleName"
                    class="tree-module mb-4"
                  >
                    <!-- Module Header with Controls -->
                    <div class="d-flex justify-space-between align-center mb-2">
                      <div class="module-header d-flex align-center">
                        <v-btn
                          icon
                          size="small"
                          variant="text"
                          @click="toggleModuleExpansion(moduleName)"
                          class="expand-btn me-2"
                        >
                          <v-icon>
                            {{ expandedModules.includes(moduleName) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                          </v-icon>
                        </v-btn>
                        <v-icon class="me-2" :color="expandedModules.includes(moduleName) ? 'primary' : 'grey'">
                          {{ expandedModules.includes(moduleName) ? 'mdi-folder-open' : 'mdi-folder' }}
                        </v-icon>
                        <v-checkbox
                          :model-value="isModuleSelected(moduleName)"
                          :indeterminate="isModuleIndeterminate(moduleName)"
                          @update:model-value="toggleModule(moduleName, $event)"
                          :label="getModuleDisplayName(moduleName)"
                          :disabled="!(editingRole ? hasPermission('admin.roles.update') : hasPermission('admin.roles.store'))"
                          density="compact"
                          hide-details
                          class="module-checkbox"
                        ></v-checkbox>
                      </div>
                      
                      <!-- Module Actions -->
                      <div v-if="editingRole ? hasPermission('admin.roles.update') : hasPermission('admin.roles.store')" class="module-actions">
                        <v-btn
                          size="small"
                          variant="text"
                          color="primary"
                          @click="selectAllModulePermissions(moduleName)"
                        >
                          تحديد الكل
                        </v-btn>
                        <v-btn
                          size="small"
                          variant="text"
                          color="error"
                          @click="deselectAllModulePermissions(moduleName)"
                        >
                          إلغاء الكل
                        </v-btn>
                      </div>
                    </div>
                    
                    <!-- Module Children (Actions) -->
                    <v-expand-transition>
                      <div v-if="expandedModules.includes(moduleName)" class="actions-list">
                        <div
                          v-for="action in actions"
                          :key="action"
                          class="tree-action d-flex align-center mb-1"
                        >
                          <div class="tree-line"></div>
                          <v-icon class="me-2" size="small" color="grey-darken-1">mdi-key</v-icon>
                          <v-checkbox
                            v-model="roleForm.permissions"
                            :value="`admin.${moduleName}.${action}`"
                            :label="getActionDisplayName(action)"
                            :disabled="!(editingRole ? hasPermission('admin.roles.update') : hasPermission('admin.roles.store'))"
                            density="compact"
                            hide-details
                            class="action-checkbox"
                          ></v-checkbox>
                        </div>
                      </div>
                    </v-expand-transition>
                  </div>
                </div>
                
                <div class="d-flex justify-space-between align-center mt-4">
                  <span class="text-body-2 text-grey">
                    {{ roleForm.permissions.length }} صلاحية محددة من أصل {{ totalPermissionsCount }}
                  </span>
                  <div v-if="editingRole ? hasPermission('admin.roles.update') : hasPermission('admin.roles.store')">
                    <v-btn
                      size="small"
                      variant="outlined"
                      color="primary"
                      @click="selectAllPermissions"
                      class="me-2"
                      :disabled="loadingPermissions"
                    >
                      تحديد جميع الصلاحيات
                    </v-btn>
                    <v-btn
                      size="small"
                      variant="outlined"
                      color="error"
                      @click="deselectAllPermissions"
                      :disabled="loadingPermissions"
                    >
                      إلغاء جميع الصلاحيات
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showRoleDialog = false">إلغاء</v-btn>
          <v-btn 
            v-if="editingRole ? hasPermission('admin.roles.update') : hasPermission('admin.roles.store')"
            color="primary" 
            @click="saveRole"
          >
            حفظ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">تأكيد الحذف</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف الدور؟
          <br><strong>{{ itemToDelete?.name }}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">إلغاء</v-btn>
          <v-btn 
            v-if="hasPermission('admin.roles.destroy')"
            color="error" 
            @click="confirmDeleteAction"
          >
            حذف
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import roleService from '@/services/roleService'
import { hasPermission, hasModule } from '@/utils/permissions'

export default {
  name: 'RolesView',
  data() {
    return {
      loading: false,
      roleSearch: '',
      showRoleDialog: false,
      editingRole: null,
      roles: [],
      roleForm: {
        name: '',
        desc: '',
        permissions: []
      },
      roleHeaders: [
        { title: 'اسم الدور', key: 'name', align: 'start' },
        { title: 'الوصف', key: 'desc', align: 'start' },
        { title: 'الصلاحيات', key: 'permissions', align: 'center' },
        { title: 'الإجراءات', key: 'actions', align: 'center', sortable: false }
      ],
      deleteDialog: false,
      itemToDelete: null,
      deleteType: '',
      // Available permissions structure - will be populated from API
      availablePermissions: {},
      loadingPermissions: false,
      // Store all permissions with their IDs for mapping
      allPermissions: [],
      // Tree expansion state
      expandedModules: []
    }
  },
  computed: {
    totalPermissionsCount() {
      return Object.values(this.availablePermissions).reduce((total, actions) => total + actions.length, 0)
    }
  },
  
  async mounted() {
    await Promise.all([
      this.loadRoles(),
      this.loadPermissions()
    ])
  },
  methods: {
    async loadRoles() {
      this.loading = true
      try {
        const response = await roleService.getRoles()
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.roles = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          this.roles = response.data
        } else {
          this.roles = []
        }
        
        //console.log('Loaded roles:', this.roles)
        // Debug: Check the structure of the first role's permissions
        if (this.roles.length > 0) {
          //console.log('First role permissions structure:', this.roles[0].permissions)
          //console.log('First role permissionNames:', this.roles[0].permissionNames)
        }
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في جلب الأدوار'
        })
      } finally {
        this.loading = false
      }
    },

    openAddRoleDialog() {
      this.editingRole = null
      this.roleForm = {
        name: '',
        desc: '',
        permissions: []
      }
      // Expand all modules by default
      this.expandedModules = Object.keys(this.availablePermissions)
      this.showRoleDialog = true
    },

    editRole(role) {
      this.editingRole = role
      
      // Handle permission IDs - convert to permission names
      let permissions = []
      if (Array.isArray(role.permissions)) {
        // If permissions is an array of IDs, convert to names
        permissions = role.permissions.map(permissionId => {
          const permission = this.allPermissions.find(p => p.id === permissionId)
          return permission ? permission.name : null
        }).filter(Boolean)
      } else if (Array.isArray(role.permissionNames)) {
        // If permissionNames is already provided
        permissions = [...role.permissionNames]
      } else if (role.permission_names && Array.isArray(role.permission_names)) {
        // Alternative field name
        permissions = [...role.permission_names]
      }
      
      //console.log('Role being edited:', role)
      //console.log('Permission IDs:', role.permissions)
      //console.log('Converted permission names:', permissions)
      
      this.roleForm = {
        name: role.name,
        desc: role.desc,
        permissions: permissions
      }
      
      // Expand all modules by default
      this.expandedModules = Object.keys(this.availablePermissions)
      this.showRoleDialog = true
    },

    async saveRole() {
      this.loading = true
      try {
        // Convert permission names back to IDs for the API
        const permissionIds = this.roleForm.permissions.map(permissionName => {
          const permission = this.allPermissions.find(p => p.name === permissionName)
          return permission ? permission.id : null
        }).filter(Boolean)
        
        const roleData = {
          name: this.roleForm.name,
          desc: this.roleForm.desc,
          permissions: permissionIds
        }
        
        //console.log('Saving role with permission IDs:', permissionIds)
        
        if (this.editingRole) {
          await roleService.updateRole(this.editingRole.id, roleData)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم تحديث الدور بنجاح'
          })
        } else {
          await roleService.createRole(roleData)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم إضافة الدور بنجاح'
          })
        }
        
        this.showRoleDialog = false
        await this.loadRoles()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حفظ الدور'
        })
      } finally {
        this.loading = false
      }
    },

    confirmDelete(item, type) {
      this.itemToDelete = item
      this.deleteType = type
      this.deleteDialog = true
    },

    async confirmDeleteAction() {
      if (!this.itemToDelete) return

      this.loading = true
      try {
        await roleService.deleteRole(this.itemToDelete.id)
        this.$store.dispatch('alerts/showAlert', {
          type: 'success',
          message: 'تم حذف الدور بنجاح'
        })
        
        this.deleteDialog = false
        await this.loadRoles()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حذف الدور'
        })
      } finally {
        this.loading = false
      }
    },

    // Tree expansion methods
    toggleModuleExpansion(moduleName) {
      const index = this.expandedModules.indexOf(moduleName)
      if (index > -1) {
        this.expandedModules.splice(index, 1)
      } else {
        this.expandedModules.push(moduleName)
      }
    },

    // Tree-like behavior methods
    isModuleSelected(moduleName) {
      const modulePermissions = this.availablePermissions[moduleName].map(action => `admin.${moduleName}.${action}`)
      return modulePermissions.every(permission => this.roleForm.permissions.includes(permission))
    },

    isModuleIndeterminate(moduleName) {
      const modulePermissions = this.availablePermissions[moduleName].map(action => `admin.${moduleName}.${action}`)
      const selectedCount = modulePermissions.filter(permission => this.roleForm.permissions.includes(permission)).length
      return selectedCount > 0 && selectedCount < modulePermissions.length
    },

    toggleModule(moduleName, selected) {
      const modulePermissions = this.availablePermissions[moduleName].map(action => `admin.${moduleName}.${action}`)
      
      if (selected) {
        // Add all module permissions
        const newPermissions = [...this.roleForm.permissions]
        modulePermissions.forEach(permission => {
          if (!newPermissions.includes(permission)) {
            newPermissions.push(permission)
          }
        })
        this.roleForm.permissions = newPermissions
      } else {
        // Remove all module permissions
        this.roleForm.permissions = this.roleForm.permissions.filter(
          permission => !modulePermissions.includes(permission)
        )
      }
    },

    // Permission checking methods
    hasPermission(permission) {
      return hasPermission(permission)
    },

    hasModule(module) {
      return hasModule(module)
    },

    // Permission management methods
    async loadPermissions() {
      this.loadingPermissions = true
      try {
        const response = await roleService.getPermissions()
        //console.log('Permissions API response:', response)
        if (response.success && response.data && Array.isArray(response.data)) {
          this.availablePermissions = this.organizePermissions(response.data)
          this.allPermissions = response.data // Store all permissions
          //console.log('Final availablePermissions:', this.availablePermissions)
        } else {
          console.warn('Unexpected permissions response structure:', response)
          this.availablePermissions = {}
          this.allPermissions = []
        }
      } catch (error) {
        console.error('Failed to load permissions:', error)
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في جلب الصلاحيات'
        })
        // Fallback to empty permissions
        this.availablePermissions = {}
        this.allPermissions = []
      } finally {
        this.loadingPermissions = false
      }
    },

    organizePermissions(permissions) {
      const organized = {}
      
      //console.log('Raw permissions from API:', permissions)
      
      permissions.forEach(permission => {
        // Handle permission name format: admin.module.action
        if (permission.name && typeof permission.name === 'string') {
          const parts = permission.name.split('.')
          
          // Ensure it's in the expected format (admin.module.action)
          if (parts.length === 3 && parts[0] === 'admin') {
            const moduleName = parts[1]
            const action = parts[2]
            
            if (!organized[moduleName]) {
              organized[moduleName] = []
            }
            
            // Avoid duplicates
            if (!organized[moduleName].includes(action)) {
              organized[moduleName].push(action)
            }
          }
        }
      })
      
      // Sort actions within each module for consistency
      Object.keys(organized).forEach(module => {
        organized[module].sort()
      })
      
      //console.log('Organized permissions:', organized)
      return organized
    },

    getModuleDisplayName(moduleName) {
      const moduleNames = {
        'mail-priorities': 'أولويات المراسلات',
        'roles': 'الأدوار',
        'mail-types': 'أنواع المراسلات',
        'file-types': 'أنواع المرفقات',
        'department-jobs': 'المناصب الإدارية',
        'employee-titles': 'المسميات الوظيفية',
        'mail-template-categories': 'فئات القوالب',
        'users': 'المستخدمين',
        'departments': 'الأقسام',
        'marginalizations': 'التهميشات',
        'mails': 'المراسلات',
        'templates': 'القوالب',
        'notes': 'الملاحظات',
        'folders': 'المجلدات',
        'dashboard': 'لوحة التحكم',
        'profile': 'الملف الشخصي',
        'settings': 'الإعدادات',
        'permissions': 'الصلاحيات',
        'attachments': 'المرفقات',
        'reports': 'التقارير',
        'logs': 'السجلات',
        'backups': 'النسخ الاحتياطية',
        'system': 'النظام',
        'archive-mails': 'الأرشيف',
        'mail-attachments': 'المرفقات',
        'mail-marginalizations': 'تهميشات المراسلات',
        'mail-templates': 'القوالب',
        'user-folders': 'مجلدات المستخدم',
        'users-departments':'تعيين المستخدمين للأقسام',

      }
      return moduleNames[moduleName] || moduleName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    getActionDisplayName(action) {
      // Ensure action is a string
      if (!action || typeof action !== 'string') {
        return action || 'غير محدد'
      }
      
      const actionNames = {
        // Basic CRUD operations
        'index': 'عرض القائمة',
        'show': 'عرض التفاصيل',
        'store': 'إضافة جديد',
        'create': 'إنشاء',
        'update': 'تعديل',
        'edit': 'تحرير',
        'destroy': 'حذف',
        'delete': 'حذف',
        
        // Mail specific actions
        'store-send': 'إرسال',
        'store-reply': 'رد',
        'store-forward': 'إعادة توجيه',
        'store-draft': 'حفظ كمسودة',
        'delete-draft': 'حذف المسودة',
        'history': 'تتبع المراسلات',
        'send-draft': 'إرسال المسودة',
        'toggle-read': 'تحديد المراسلات المقروءة',
        'toggle-star': 'تحديد المراسلات المهمة',
        'update-draft': 'تحديث المسودة',
        'mark-as-printed': 'تحديد المراسلات المطبوعة',
        'store-archive': 'أرشفة',
        'restore': 'استعادة',
        'print': 'طباعة',
        'download': 'تحميل',
        'attach': 'إرفاق ملف',
        'detach': 'إزالة مرفق',
        
        // User management
        'activate': 'تفعيل',
        'deactivate': 'إلغاء تفعيل',
        'suspend': 'تعليق',
        'unsuspend': 'إلغاء التعليق',
        'reset-password': 'إعادة تعيين كلمة المرور',
        'change-password': 'تغيير كلمة المرور',
        'impersonate': 'تسجيل دخول بالنيابة',
        
        // Role and permission management
        'assign': 'تعيين',
        'unassign': 'إلغاء تعيين',
        'grant': 'منح صلاحية',
        'revoke': 'سحب صلاحية',
        
        // Department and organization
        'transfer': 'نقل',
        'move': 'نقل',
        'approve': 'موافقة',
        'reject': 'رفض',
        'review': 'مراجعة',
        
        // File and document management
        'upload': 'رفع',
        'export': 'تصدير',
        'import': 'استيراد',
        'backup': 'نسخ احتياطي',
        'sync': 'مزامنة',
        
        // System and settings
        'configure': 'تكوين',
        'maintain': 'صيانة',
        'monitor': 'مراقبة',
        'audit': 'تدقيق',
        'log': 'تسجيل',
        'report': 'تقرير',
        
        // Advanced operations
        'bulk-action': 'عمليات مجمعة',
        'mass-update': 'تحديث شامل',
        'mass-delete': 'حذف شامل',
        'clone': 'استنساخ',
        'duplicate': 'تكرار',
        'merge': 'دمج',
        'split': 'تقسيم',
        
        // Workflow and process
        'start': 'بدء',
        'stop': 'إيقاف',
        'pause': 'إيقاف مؤقت',
        'resume': 'استكمال',
        'cancel': 'إلغاء',
        'complete': 'إنهاء',
        
        // Security and access
        'lock': 'قفل',
        'unlock': 'إلغاء القفل',
        'encrypt': 'تشفير',
        'decrypt': 'فك التشفير',
        'secure': 'تأمين',
        
        // Communication
        'notify': 'إشعار',
        'message': 'رسالة',
        'broadcast': 'بث',
        'announce': 'إعلان',
        
        // Data management
        'search': 'بحث',
        'filter': 'تصفية',
        'sort': 'ترتيب',
        'paginate': 'تقسيم الصفحات',
        'validate': 'التحقق',
        'verify': 'التأكد'
      }
      
      return actionNames[action] || action.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    selectAllModulePermissions(moduleName) {
      const moduleActions = this.availablePermissions[moduleName]
      if (moduleActions) {
        const modulePermissions = moduleActions.map(action => `admin.${moduleName}.${action}`)
        
        // Remove existing permissions for this module first
        this.roleForm.permissions = this.roleForm.permissions.filter(
          permission => !permission.startsWith(`admin.${moduleName}.`)
        )
        // Add all permissions for this module
        this.roleForm.permissions = [...this.roleForm.permissions, ...modulePermissions]
        // Remove duplicates
        this.roleForm.permissions = [...new Set(this.roleForm.permissions)]
      }
    },

    deselectAllModulePermissions(moduleName) {
      this.roleForm.permissions = this.roleForm.permissions.filter(
        permission => !permission.startsWith(`admin.${moduleName}.`)
      )
    },

    selectAllPermissions() {
      const allPermissions = []
      Object.keys(this.availablePermissions).forEach(moduleName => {
        this.availablePermissions[moduleName].forEach(action => {
          allPermissions.push(`admin.${moduleName}.${action}`)
        })
      })
      this.roleForm.permissions = allPermissions
    },

    deselectAllPermissions() {
      this.roleForm.permissions = []
    },

    getPermissionCount(item) {
      if (Array.isArray(item.permissions)) {
        return item.permissions.length;
      } else if (Array.isArray(item.permissionNames)) {
        return item.permissionNames.length;
      } else if (item.permission_names && Array.isArray(item.permission_names)) {
        return item.permission_names.length;
      }
      return 0;
    },

    getPermissionsList(item) {
      let permissionsList = [];
      
      if (Array.isArray(item.permissions)) {
        // Handle permission IDs - convert to names and then to display names
        permissionsList = item.permissions.map(permissionId => {
          const permission = this.allPermissions.find(p => p.id === permissionId)
          if (permission && permission.name) {
            const parts = permission.name.split('.')
            const action = parts.length === 3 ? parts[2] : permission.name
            return this.getActionDisplayName(action)
          }
          return null
        }).filter(Boolean)
      } else if (Array.isArray(item.permissionNames)) {
        permissionsList = item.permissionNames.map(perm => {
          if (typeof perm === 'string') {
            const parts = perm.split('.')
            const action = parts.length === 3 ? parts[2] : perm
            return this.getActionDisplayName(action)
          }
          return 'غير محدد'
        }).filter(Boolean)
      } else if (item.permission_names && Array.isArray(item.permission_names)) {
        permissionsList = item.permission_names.map(perm => {
          if (typeof perm === 'string') {
            const parts = perm.split('.')
            const action = parts.length === 3 ? parts[2] : perm
            return this.getActionDisplayName(action)
          }
          return 'غير محدد'
        }).filter(Boolean)
      }
      
      // Remove duplicates and join
      const uniquePermissions = [...new Set(permissionsList)]
      return uniquePermissions.join(', ') || 'لا توجد صلاحيات'
    },

    getPermissionDisplayName(permissionName) {
      if (!permissionName || typeof permissionName !== 'string') {
        return 'غير محدد'
      }
      
      const parts = permissionName.split('.')
      if (parts.length === 3) {
        const moduleName = parts[1]
        const action = parts[2]
        return `${this.getModuleDisplayName(moduleName)} - ${this.getActionDisplayName(action)}`
      }
      
      return permissionName
    }
  }
}
</script>

<style scoped>
.roles-settings {
  min-height: 100vh;
}

.permissions-tree {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  background-color: #fafafa;
}

.tree-module {
  border-left: 3px solid #e0e0e0;
  padding-left: 8px;
  margin-bottom: 16px;
}

.tree-module:last-child {
  margin-bottom: 0;
}

.module-header {
  align-items: center;
}

.expand-btn {
  min-width: 24px !important;
  width: 24px;
  height: 24px;
}

.module-checkbox :deep(.v-label) {
  font-weight: 600;
  color: #1976d2;
  font-size: 1rem;
}

.module-actions {
  display: flex;
  gap: 4px;
}

.actions-list {
  margin-left: 40px;
  padding-top: 8px;
}

.tree-action {
  position: relative;
  padding-left: 24px;
}

.tree-line {
  position: absolute;
  left: 0;
  top: 50%;
  width: 16px;
  height: 1px;
  background-color: #ccc;
}

.tree-line::before {
  content: '';
  position: absolute;
  left: -24px;
  top: -12px;
  width: 1px;
  height: 24px;
  background-color: #ccc;
}

.action-checkbox :deep(.v-label) {
  font-size: 0.875rem;
  color: #666;
}

/* Last action line styling */
.tree-action:last-child .tree-line::before {
  height: 12px;
}
</style>